import { supabase } from '../lib/supabase';

/**
 * Order Service for creating and managing taxi orders in Supabase
 * Handles snake_case (DB) ↔ camelCase (React) conversion
 * FIXED: Now uses user_profiles table instead of direct auth.users access
 * ENHANCED: Added defensive price validation
 */
export const orderService = {
  /**
   * Create a new order
   * @param {Object} orderData - Order data in camelCase
   * @returns {Promise<Object>} Created order in camelCase
   */
  async createOrder(orderData) {
    try {
      // CRITICAL VALIDATION: Check that fareAmount is provided and valid
      if (!orderData?.fareAmount || orderData?.fareAmount === null || orderData?.fareAmount === undefined) {
        throw new Error('Price is required. Please ensure a service is selected and price is calculated.');
      }

      // Validate that fareAmount is a positive number
      const fareAmount = parseFloat(orderData?.fareAmount);
      if (isNaN(fareAmount) || fareAmount <= 0) {
        throw new Error('Invalid price value. Please try selecting service again.');
      }

      // Get current session - more reliable than getUser()
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      // Determine if this is an authenticated or guest booking
      const isAuthenticated = !!session?.user && !sessionError;
      
      console.log('Order creation - Auth status:', {
        isAuthenticated,
        authUserId: session?.user?.id,
        sessionError: sessionError?.message,
        fareAmount: fareAmount // Log validated price
      });

      let customerProfileId = null;

      // If user is authenticated, fetch their profile ID from user_profiles
      if (isAuthenticated) {
        try {
          const { data: profile, error: profileError } = await supabase
            ?.from('user_profiles')
            ?.select('id')
            ?.eq('id', session?.user?.id)
            ?.single();

          if (profileError) {
            console.error('Profile fetch error:', profileError);
            // If profile doesn't exist or error occurs, treat as guest bookingconsole.warn('User profile not found, creating guest booking');
          } else if (profile?.id) {
            customerProfileId = profile?.id;
            console.log('Found customer profile ID:', customerProfileId);
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
          // Treat as guest booking if profile lookup fails
        }
      }

      // Convert camelCase to snake_case for database
      const dbOrder = {
        // Only include customer_id if we found a valid profile
        ...(customerProfileId && { customer_id: customerProfileId }),
        pickup_location: orderData?.pickupLocation,
        dropoff_location: orderData?.dropoffLocation || orderData?.destination,
        customer_name: orderData?.customerName || orderData?.customer_name || 'Guest',customer_phone: orderData?.customerPhone || orderData?.customer_phone || '',
        fare_amount: fareAmount, // Use validated price
        status: 'pending'
      };

      console.log('Creating order with validated data:', {
        ...dbOrder,
        isGuestBooking: !customerProfileId,
        priceValidated: true
      });

      // Insert into database
      const { data, error } = await supabase
        ?.from('orders')
        ?.insert(dbOrder)
        ?.select()
        ?.single();

      if (error) {
        console.error('Database error:', error);
        throw new Error(`Failed to create order: ${error?.message}`);
      }

      if (!data) {
        throw new Error('Order creation returned no data');
      }

      console.log('Order created successfully:', {
        orderId: data?.id,
        customerId: data?.customer_id,
        fareAmount: data?.fare_amount,
        isGuestOrder: !data?.customer_id
      });

      // Convert snake_case back to camelCase for React
      return {
        id: data?.id,
        customerId: data?.customer_id,
        driverId: data?.driver_id,
        pickupLocation: data?.pickup_location,
        dropoffLocation: data?.dropoff_location,
        customerName: data?.customer_name,
        customerPhone: data?.customer_phone,
        status: data?.status,
        fareAmount: data?.fare_amount,
        assignedAt: data?.assigned_at,
        completedAt: data?.completed_at,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Order creation error:', error);
      throw error;
    }
  },

  /**
   * Get all orders for current user
   * @returns {Promise<Array>} Array of orders in camelCase
   */
  async getMyOrders() {
    try {
      // Get user's profile ID from user_profiles table
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch profile to get the correct profile ID
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('id', user?.id)
        ?.single();

      if (profileError || !profile) {
        throw new Error('User profile not found');
      }

      const { data, error } = await supabase
        ?.from('orders')
        ?.select('*')
        ?.eq('customer_id', profile?.id)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Convert to camelCase
      return data?.map(order => ({
        id: order?.id,
        customerId: order?.customer_id,
        driverId: order?.driver_id,
        pickupLocation: order?.pickup_location,
        dropoffLocation: order?.dropoff_location,
        customerName: order?.customer_name,
        customerPhone: order?.customer_phone,
        status: order?.status,
        fareAmount: order?.fare_amount,
        assignedAt: order?.assigned_at,
        completedAt: order?.completed_at,
        createdAt: order?.created_at,
        updatedAt: order?.updated_at
      }));
    } catch (error) {
      console.error('Get orders error:', error);
      throw error;
    }
  },

  /**
   * Get order by ID
   * @param {string} orderId - Order ID
   * @returns {Promise<Object>} Order in camelCase
   */
  async getOrderById(orderId) {
    try {
      const { data, error } = await supabase
        ?.from('orders')
        ?.select('*')
        ?.eq('id', orderId)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        customerId: data?.customer_id,
        driverId: data?.driver_id,
        pickupLocation: data?.pickup_location,
        dropoffLocation: data?.dropoff_location,
        customerName: data?.customer_name,
        customerPhone: data?.customer_phone,
        status: data?.status,
        fareAmount: data?.fare_amount,
        assignedAt: data?.assigned_at,
        completedAt: data?.completed_at,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Get order error:', error);
      throw error;
    }
  }
};